import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  Dialog,
  ModalPortal,
  CircularButton,
  Icon,
  icons,
  Link,
} from '@hoffmann/pattern-library-2';

import { callIfFunction } from '../../utils/callIfFunction';
import { TechnicalAboutModal } from '../TechnicalAboutModal/TechnicalAboutModal';

interface Props {
  onOpenConditions: () => void;
  onClose: () => void;
  t: (key: string, args?: any) => string;
}

export const AboutUsModal: React.FC<Props> = ({ onClose, onOpenConditions, t }) => {
  const [altPressed, setAltPressed] = useState<boolean>(false);
  const [aboutAppOpen, setAboutAppOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = ({ key = '' }: KeyboardEvent) => {
      setAltPressed(key.toLowerCase() === 'alt');
    };
    const onKeyUp = ({ key = '' }: KeyboardEvent) => {
      if (key.toLowerCase() === 'alt') {
        setAltPressed(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const handleDspClick = useCallback(() => {
    if (altPressed) {
      setAboutAppOpen(true);
    }
  }, [altPressed]);

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'escape') {
        callIfFunction(onClose);
      }
    };
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [onClose]);

  return (
    <ModalPortal isOpen={true} onClose={onClose}>
      <Dialog
        title={t('ABOUT_US_TITLE')}
        size={200}
        headerAction={
          <CircularButton icon={<Icon source={icons.Close} />} ghost onClick={onClose} />
        }
        hasScroll
      >
        <Content>
          <div onClick={handleDspClick}>{t('BUTTON_DSP')}</div>
          <div>{t('ABOUT_US_RIGHTS', { year: new Date().getFullYear() })}</div>
          <Links>
            <Link
              onClick={() => {
                onOpenConditions();
                onClose();
              }}
            >
              {t('ABOUT_US_CONDITIONS')}
            </Link>
            <Link href={t('URL_OPENSOURCE')} target="_blank">
              {t('ABOUT_US_OPENSOURCE')}
            </Link>
          </Links>
          {aboutAppOpen && <TechnicalAboutModal onClose={() => setAboutAppOpen(false)} />}
        </Content>
      </Dialog>
    </ModalPortal>
  );
};

const Content = styled.div`
  font-size: 12px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.layout50};
`;

const Links = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;

  * + * {
    margin-left: 24px;
  }
`;
